import React from "react";
import {
  makeStyles,
  tokens,
  Card,
  Text,
  Button,
} from "@fluentui/react-components";
import { ErrorCircle24Regular } from "@fluentui/react-icons";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalXXL,
  },
  card: {
    padding: tokens.spacingVerticalXXL,
    textAlign: "center",
    maxWidth: "500px",
  },
  icon: {
    color: tokens.colorPaletteRedForeground1,
    marginBottom: tokens.spacingVerticalS,
  },
});

interface ErrorBoundaryState {
  hasError: boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
const ErrorFallback: React.FC<{ onReset: () => void }> = ({ onReset }) => {
  const styles = useStyles();

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <ErrorCircle24Regular className={styles.icon} />
        <Text as="h2" size={500} weight="semibold" block>
          Something went wrong
        </Text>
        <Text block>
          An unexpected error occurred. Please try refreshing the page.
        </Text>
        <Button
          appearance="primary"
          onClick={onReset}
          style={{ marginTop: tokens.spacingVerticalL }}
        >
          Refresh Page
        </Button>
      </Card>
    </div>
  );
};

export class ErrorBoundary extends React.Component<
  React.PropsWithChildren,
  ErrorBoundaryState
> {
  constructor(props: React.PropsWithChildren) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  handleReset = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return <ErrorFallback onReset={this.handleReset} />;
    }
    return this.props.children;
  }
}
